import Events from 'events';

import Dispatcher from './Dispatcher';
import Constants from './Constants';
import { base64ToFile } from "../Helpers";
import checkStatus from '../utils/checkStatus';
import CountryStore from './CountryStore';
import AppActions from './AppActions';
import LanguageStore from './LanguageStore';

// @todo 2017-07-26 Abort running requests for a preset if a new is requested

const EventEmitter = Events.EventEmitter;

const EVENTS = {
  GET_PRODUCTS: 'GET_PRODUCTS',
  GET_PRODUCT: 'GET_PRODUCT',
  SET_PRODUCT: 'SET_PRODUCT',
  GET_PRODUCT_BY_HASH: 'GET_PRODUCT_BY_HASH',
  GET_PRESETS: 'GET_PRESETS',
  GET_CUSTOM_PRODUCT: 'GET_CUSTOM_PRODUCT',
  CREATE_CUSTOM_PRODUCT: 'CREATE_CUSTOM_PRODUCT',
  UPDATE_CUSTOM_PRODUCT: 'UPDATE_CUSTOM_PRODUCT',
  UPDATE_CUSTOM_PRODUCT_FIT: 'UPDATE_CUSTOM_PRODUCT_FIT',
  BOOK_APPOINTMENT: 'BOOK_APPOINTMENT',
  GET_STORES: 'GET_STORES',
};

const MODELS = {
  productList: {},
  products: {},
  customProducts: {},
  presets: {},
  stores: {},
};

const fetching = [];

// https://github.com/github/fetch#handling-http-error-statuses
function parseJSON(response) {
  return response.json()
}

function formatCustomProduct(customProduct) {
  const specification = customProduct.specification.map(s => ({ partCode: s[0], material: s[1] }));

  return Object.assign(customProduct, { specification });
}

function uploadFile(action, formData) {
  formData.append('action', action);

  return fetch(`${Constants.BASE_URL}/resource`, {
    method: 'POST',
    body: formData
  }).then(checkStatus).then(parseJSON).catch(error => console.error);
}

async function uploadSpecificationFile(hash, part) {
  const formData = new FormData();

  formData.append('part_code', part.key);
  formData.append('custom_product_id', hash);

  // @todo 2017-12-11 Should be part.file === null?
  if ( ! part.file) {
    formData.append('_method', 'DELETE');
    return uploadFile(`custom_patch:${part.type}`, formData);
  }

  return base64ToFile(part.file).then(file => {
    formData.append('file', file);

    return uploadFile(`custom_patch:${part.type}`, formData);
  });
}

function uploadSpecificationText(hash, part) {
  const formData = new FormData();

  formData.append('part_code', part.key);
  formData.append('custom_product_id', hash);

  if (part.text) {
    formData.append('_method', 'PATCH');
    formData.append('text', part.text.text);
    formData.append('material', part.text.material);
    formData.append('stroke_width', +part.text.stroke_width);

    if (part.text.stroke_dimension !== null) {
      formData.append('stroke_dimension', part.text.stroke_dimension)
    }

    if (part.text.stroke_material) {
      formData.append('stroke_material', part.text.stroke_material);
    }

    // formData.append('bold', +part.text.bold);
    // formData.append('italic', +part.text.italic);
    formData.append('font_size', +part.text.font_size);
    formData.append('font_dimension', part.text.font_dimension);
    formData.append('font', part.text.font);
  } else {
    formData.append('_method', 'DELETE');
  }

  return fetch(`${Constants.BASE_URL}/texts`, {
    method: 'POST',
    body: formData
  }).then(checkStatus).then(parseJSON).catch(error => console.error);
}

function uploadScreenshotFile(side, hash, file) {
  const formData = new FormData();

  formData.append('custom_product_id', hash);

  return base64ToFile(file).then(file => {
    formData.append('file', file);

    return uploadFile(`custom_product:screenshot_${side}`, formData);
  });
}

function excludeNonDirtySpecificationResource(resources) {
  return resources.filter(resource => resource.dirty === true);
}

async function uploadSpecificationResources(hash, files, texts, screenshots) {
  for (const file of files) {
    await uploadSpecificationFile(hash, file);
  }

  for (const text of texts) {
    await uploadSpecificationText(hash, text);
  }

  await uploadScreenshotFile('front', hash, screenshots.front);
  await uploadScreenshotFile('back', hash, screenshots.back);
  await uploadScreenshotFile('left', hash, screenshots.left);
  await uploadScreenshotFile('right', hash, screenshots.right);

  return true;
}

const Store = Object.assign({}, EventEmitter.prototype, {
  _getProducts(category) {
    const country = CountryStore.getState();

    if (fetching[`${Constants.BASE_URL}/product?country=${country}`]) {
      console.warn('Already fetching');
      return;
    }

    fetching[`${Constants.BASE_URL}/product?country=${country}`] = true;

    return fetch(`${Constants.BASE_URL}/product?country=${country}`)
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        const sortByPrice = (p1, p2) => p2.base_price - p1.base_price;

        const addCountryId = (product, country) => {
          product.country_id = country;
          return product;
        };

        const groupByCategory = (carry, product) => {
          if (carry[product.category]) {
            carry[product.category].push(product);
          } else {
            carry[product.category] = [product];
          }

          return carry;
        };

        MODELS.productList = response
          .sort(sortByPrice)
          .map(product => addCountryId(product, country))
          .reduce(groupByCategory, {});

        this.events.getProducts.emit(category);
        fetching[`${Constants.BASE_URL}/product?country=${country}`] = false;
      })
      .catch(error => {
        this.events.getProducts.emit(category, error);
      });
  },

  _getProduct(code) {
    return fetch(`${Constants.BASE_URL}/product/${code}?country=${CountryStore.getState()}`)
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        MODELS.products[code] = response;

        this.events.getProduct.emit(code);
      })
      .catch(error => {
        this.events.getProduct.emit(code, error);
      });
  },

  _setProduct(productCode, productSpecification) {
    MODELS.products = [];
    MODELS.products[productCode] = productSpecification;
    this.events.setProduct.emit(productCode);
  },

  _getProductByHash(hash) {
    return this._getCustomProduct(hash).then(customProduct => {
      if ( ! customProduct) {
        throw new TypeError("Custom product not found.");
      }

      return customProduct;
    }).then(customProduct => {
      AppActions.updateCountry(customProduct.country_id);

      const promises = [
        this._getProduct(customProduct.product.code),
        this._getPresets(customProduct.product.code)
      ];

      Promise.all(promises).then(() => {
        this.events.getProductByHash.emit(customProduct.product.code, customProduct.preset, customProduct.hash);
      });
    }).catch(error => {
      console.error(error);
      this.events.getProductByHash.emit(null, null, hash, error);
    });
  },

  _getPresets(productCode) {
    return fetch(`https://emersya.com/API/configurator/getPresets/${productCode}`)
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        let presets = {};
        let data = response['resultSet']['data']['0'];

        for (let idx in data) {
          for (let idxSet in data[idx].settings) {
            if ( ! presets[data[idx].name]) {
              presets[data[idx].name] = {
                name: data[idx].name,
                productCode,
                small_preview_path: data[idx].small_preview_path,
                big_preview_path: data[idx].big_preview_path,
                specification: [],
                files: [],
                texts: [],
              };
            }

            presets[data[idx].name].specification.push({
              partCode: data[idx].settings[idxSet][0],
              material: data[idx].settings[idxSet][1],
            });
          }
        }

        MODELS.presets[productCode] = presets;
        this.events.getPresets.emit(productCode);
      })
      .catch(error => {
        this.events.getPresets.emit(productCode, error);
      })
  },

  _getCustomProduct(hash) {
    return fetch(`${Constants.BASE_URL}/custom-product/${hash}`)
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        const customProduct = formatCustomProduct(response);

        MODELS.customProducts[hash] = customProduct;
        this.events.getCustomProduct.emit(hash);

        return customProduct;
      })
      .catch(error => {
        console.log(error)
        this.events.getCustomProduct.emit(hash, error);
      });
  },

  _createCustomProduct(specification, extra) {
    // https://github.com/github/fetch/issues/89#issuecomment-82397221
    // request.upload.onprogress = event => {}


    const formData = new FormData();

    formData.append('productCode', specification.productCode);
    formData.append('productPresetName', specification.productPresetName);
    formData.append('perforated', specification.perforated ? '1' : '0');
    formData.append('custom_colored', specification.custom_colored ? '1' : '0');
    formData.append('country_id', CountryStore.getState());
    formData.append('email', extra.email);
    formData.append('status', extra.status);

    if (!specification.custom_fitted){
        formData.append('size', specification.size);
        formData.append('size_type', specification.sizeType);
    }
    if (specification.custom_colored) {
      specification.specification.forEach(part => {
        if (part.type !== undefined) {
          formData.append(`specification[${part.key}][type]`, part.type);
        }

        formData.append(`specification[${part.key}][material]`, part.material);
      });
    }

    return fetch(`${Constants.BASE_URL}/custom-product`, {
      method: 'POST',
      body: formData
    }).then(checkStatus).then(parseJSON).then(async customProduct => {
      if (specification.custom_colored) {
        await uploadSpecificationResources(
          customProduct.hash,
          specification.files,
          specification.texts,
          specification.screenshots
        );
      }
      await this._getCustomProduct(customProduct.hash);

      this.events.createCustomProduct.emit(customProduct.hash);
    }).catch(

      error => {this.events.createCustomProduct.emit(null, error)}
    );
  },

  _updateCustomProduct(hash, specification) {
    // https://github.com/github/fetch/issues/89#issuecomment-82397221
    // request.upload.onprogress = event => {};

    const formData = new FormData();

    formData.append('_method', 'PATCH');
    formData.append('productPresetName', specification.productPresetName);
    formData.append('perforated', specification.perforated ? 1 : 0);
    formData.append('size_type', specification.sizeType);

    specification.specification.forEach(part => {
      if (part.type !== undefined) {
        formData.append(`specification[${part.key}][type]`, part.type);
      }

      formData.append(`specification[${part.key}][material]`, part.material);
    });

    return fetch(`${Constants.BASE_URL}/custom-product/${hash}`, {
      method: 'POST',
      body: formData
    }).then(checkStatus).then(parseJSON).then(async customProduct => {
      await uploadSpecificationResources(
        customProduct.hash,
        excludeNonDirtySpecificationResource(specification.files),
        excludeNonDirtySpecificationResource(specification.texts),
        specification.screenshots
      );
      await this._getCustomProduct(customProduct.hash);

      // We emit a "createCustomProduct" because "customProduct" should be
      // treated as a just created product.
      this.events.createCustomProduct.emit(customProduct.hash);
    }).catch(error => console.error);
  },

  _updateCustomProductFit(hash, customFitted, size, sizeType) {
    const formData = new FormData();

    formData.append('_method', 'PATCH');
    formData.append('customFitted', +customFitted);
    formData.append('size', size);
    formData.append('sizeType', sizeType);

    return fetch(`${Constants.BASE_URL}/custom-product/${hash}`, {
      method: 'POST',
      body: formData,
    }).then(checkStatus).then(parseJSON).then(response => {
      this.events.updateCustomProductFit.emit(hash);
    }).catch(error => console.error);
  },

  _bookAppointment(data) {
    const formData = new FormData();

    // Adds each item of the "data" Object to the FormData
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    //add language to translate emails
    formData.append('lang', LanguageStore.getState());

    return fetch(`${Constants.BASE_URL}/appointments`, {
      method: 'POST',
      body: formData
    }).then(checkStatus).then(parseJSON).then(() => {
      this.events.bookAppointment.emit(data);
    }).catch(error => {
      this.events.bookAppointment.emit(data, error);
    });
  },

  _getStores() {
    return fetch(`${Constants.BASE_URL}/stores?country=${CountryStore.getState()}`).then(checkStatus).then(parseJSON).then(stores => {
      MODELS.stores = stores;
      this.events.getStores.emit();
    }).catch(error => {
      this.events.getStores.emit(error);
    });
  },

  getProducts(category = null) {
    return category
      ? MODELS.productList[category]
      : MODELS.productList;
  },

  getProduct(productCode) {
    return productCode ? MODELS.products[productCode] : null;
  },

  getPresets(productCode, presetName = null) {
    if (presetName) {
      return MODELS.presets[productCode][presetName];
    }

    return MODELS.presets[productCode];
  },

  getCustomProduct(hash) {
    return hash ? MODELS.customProducts[hash] : null;
  },

  getStores() {
    return MODELS.stores;
  },

  events: {
    getProducts: {
      emit(category, errors) {
        Store.emit(EVENTS.GET_PRODUCTS, category, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.GET_PRODUCTS, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.GET_PRODUCTS, callback);
      }
    },

    getProduct: {
      emit(code, errors) {
        Store.emit(EVENTS.GET_PRODUCT, code, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.GET_PRODUCT, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.GET_PRODUCT, callback);
      }
    },

    setProduct: {
      emit(productCode, productSpecification, errors) {
        Store.emit(EVENTS.SET_PRODUCT, productCode, productSpecification, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.SET_PRODUCT, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.SET_PRODUCT, callback);
      }
    },

    getProductByHash: {
      emit(code, preset, hash, errors) {
        Store.emit(EVENTS.GET_PRODUCT_BY_HASH, code, preset, hash, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.GET_PRODUCT_BY_HASH, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.GET_PRODUCT_BY_HASH, callback);
      }
    },

    getPresets: {
      emit(code, errors) {
        Store.emit(EVENTS.GET_PRESETS, code, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.GET_PRESETS, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.GET_PRESETS, callback);
      }
    },

    getCustomProduct: {
      emit(hash, errors) {
        Store.emit(EVENTS.GET_CUSTOM_PRODUCT, hash, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.GET_CUSTOM_PRODUCT, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.GET_CUSTOM_PRODUCT, callback);
      }
    },

    createCustomProduct: {
      emit(hash, errors) {

        Store.emit(EVENTS.CREATE_CUSTOM_PRODUCT, hash, errors);
      },
      addListener(callback) {

        Store.on(EVENTS.CREATE_CUSTOM_PRODUCT, callback);
      },
      removeListener(callback) {

        Store.removeListener(EVENTS.CREATE_CUSTOM_PRODUCT, callback);
      }
    },

    updateCustomProductFit: {
      emit(hash, errors) {
        Store.emit(EVENTS.UPDATE_CUSTOM_PRODUCT_FIT, hash, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.UPDATE_CUSTOM_PRODUCT_FIT, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.UPDATE_CUSTOM_PRODUCT_FIT, callback);
      }
    },

    bookAppointment: {
      emit(data, errors) {
        Store.emit(EVENTS.BOOK_APPOINTMENT, data, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.BOOK_APPOINTMENT, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.BOOK_APPOINTMENT, callback);
      }
    },

    getStores: {
      emit(errors) {
        Store.emit(EVENTS.GET_STORES, errors);
      },
      addListener(callback) {
        Store.on(EVENTS.GET_STORES, callback);
      },
      removeListener(callback) {
        Store.removeListener(EVENTS.GET_STORES, callback);
      }
    }
  },
  ___actionDispatcher(action) {
    switch(action.actionType) {
      case Constants.UPDATE_COUNTRY:
        MODELS.productList = {};

        Dispatcher.waitFor([CountryStore.getDispatchToken()]);

        Store.events.getProducts.emit(null);
        break;

      case Constants.GET_PRODUCTS:
        return Store._getProducts(action.category);

      case Constants.GET_PRODUCT:
        return Store._getProduct(action.code);

      case Constants.SET_PRODUCT:
        return Store._setProduct(action.productCode, action.productSpecification);

      case Constants.GET_PRODUCT_BY_HASH:
        return Store._getProductByHash(action.hash);

      case Constants.GET_PRESETS:
        return Store._getPresets(action.code);

      case Constants.GET_CUSTOM_PRODUCT:
        return Store._getCustomProduct(action.hash);

      case Constants.CREATE_CUSTOM_PRODUCT:
        return Store._createCustomProduct(action.specification, action.extra);

      case Constants.UPDATE_CUSTOM_PRODUCT:
        return Store._updateCustomProduct(action.hash, action.specification);

      case Constants.UPDATE_CUSTOM_PRODUCT_FIT:
        return Store._updateCustomProductFit(action.hash, action.customFitted, action.size, action.sizeType);

      case Constants.BOOK_APPOINTMENT:
        return Store._bookAppointment(action.data);

      case Constants.GET_STORES:
        return Store._getStores();

      default:
        // console.warn('Invalid action', action);
    }
  }
});

/* --- */

Dispatcher.register(Store.___actionDispatcher);

export default Store;



// WEBPACK FOOTER //
// ./src/flux/Store.js